<template>
  <div class="mc-portal">
    <PortalTopBar :role="barRole" />

    <div class="pt-container">
      <div class="pt-head">
        <div>
          <div class="pt-kicker">Onboard a city</div>
          <h1 class="pt-title">Set up a new city</h1>
          <p class="pt-sub">Creates the city's portal, its contract record and a login for your main contact, who gets an email to set a password.</p>
        </div>
      </div>

      <!-- Done -->
      <section v-if="done" class="pt-card done">
        <span class="stamp">ONBOARDED</span>
        <h2>{{ form.cityName }} is set up</h2>
        <p>{{ form.contactName || "The contact" }} can sign in once they set a password.</p>
        <div class="done-list">
          <div class="pt-notice" :class="done.emailSent ? 'info' : 'warn'">
            <SiteIcon :name="done.emailSent ? 'check' : 'alert'" :size="17" />
            <span v-if="done.emailSent">Welcome email sent to {{ form.contactEmail }}.</span>
            <span v-else>The account was created, but the welcome email didn't send: {{ done.emailError || "unknown error" }}. The contact can use "Forgot password" on the sign-in page. <template v-if="/allowlist|authorized|continue/i.test(done.emailError || '')">To fix it, add your site's domain under Firebase Authentication → Settings → Authorized domains.</template><template v-else>Check the Resend setup (API key and verified sending domain) in the function logs.</template></span>
          </div>
          <div v-if="form.sendSetupInvoice && form.setupFee > 0" class="pt-notice" :class="done.setupInvoiceUrl ? 'info' : 'warn'">
            <SiteIcon :name="done.setupInvoiceUrl ? 'check' : 'alert'" :size="17" />
            <span v-if="done.setupInvoiceUrl">Setup fee invoice ({{ money(form.setupFee) }}<template v-if="hardwareOnInvoice"> + {{ money(hardwareTotal) }} hardware</template>) emailed. <a :href="done.setupInvoiceUrl" target="_blank" rel="noopener" class="pt-textlink">View it</a></span>
            <span v-else>The setup fee invoice didn't send: {{ done.setupInvoiceError || "unknown error" }}. You can send it from the city page.</span>
          </div>
          <div v-if="form.leadId" class="pt-notice info"><SiteIcon name="trophy" :size="17" /><span>The lead was marked won.</span></div>
        </div>
        <div class="done-actions">
          <router-link v-if="barRole === 'admin'" :to="`/admin/cities/${done.companyId}`" class="pt-btn pt-btn-primary">Open the city</router-link>
          <router-link :to="`/manage-app-progress?city=${done.companyId}`" class="pt-btn pt-btn-outline">Start the rollout</router-link>
          <button type="button" class="pt-btn pt-btn-ghost" @click="startOver">Onboard another city</button>
        </div>
      </section>

      <div v-else class="grid">
        <section class="pt-card wizard">
          <ol class="steps">
            <li v-for="(s, i) in steps" :key="s" :class="{ on: step === i, done: step > i }">
              <button type="button" :disabled="i > maxReached" @click="step = i">
                <span class="n"><SiteIcon v-if="step > i" name="check" :size="14" /><template v-else>{{ i + 1 }}</template></span>
                <span class="l">{{ s }}</span>
              </button>
            </li>
          </ol>

          <!-- 1. City -->
          <div v-if="step === 0" class="panel">
            <div v-if="leads.length" class="pt-field">
              <span class="pt-label">Start from one of your leads <em>(optional)</em></span>
              <select v-model="form.leadId" class="pt-select" @change="applyLead">
                <option value="">Don't link a lead</option>
                <option v-for="l in leads" :key="l.id" :value="l.id">{{ l.companyName }}{{ l.state ? `, ${l.state}` : "" }} · {{ stageName(l.status) }}</option>
              </select>
              <span class="pt-hint">Fills in what you already know and marks the lead won when you finish.</span>
            </div>
            <div class="pt-grid-2">
              <label class="pt-field"><span class="pt-label">City or town name *</span><input v-model.trim="form.cityName" class="pt-input" placeholder="Town of Westbrook" /></label>
              <label class="pt-field"><span class="pt-label">State *</span><input v-model.trim="form.state" class="pt-input" placeholder="MA" maxlength="40" /></label>
              <label class="pt-field"><span class="pt-label">Population</span><input v-model.number="form.population" class="pt-input" type="number" min="0" placeholder="18000" @input="tierTouched || applyTier()" /></label>
              <label class="pt-field"><span class="pt-label">Website</span><input v-model.trim="form.website" class="pt-input" type="url" placeholder="https://westbrook-ma.gov" /></label>
            </div>
            <label class="pt-field"><span class="pt-label">Town hall street address</span><input v-model.trim="form.street" class="pt-input" placeholder="1 Main Street" /></label>
            <div class="pt-grid-2">
              <label class="pt-field"><span class="pt-label">Town <em>(if different)</em></span><input v-model.trim="form.addrCity" class="pt-input" :placeholder="form.cityName.replace(/^(Town|City) of /i, '') || 'Westbrook'" /></label>
              <label class="pt-field"><span class="pt-label">ZIP</span><input v-model.trim="form.zip" class="pt-input" placeholder="01581" /></label>
            </div>
          </div>

          <!-- 2. Contact -->
          <div v-if="step === 1" class="panel">
            <div class="pt-notice info"><SiteIcon name="info" :size="17" /><span>This person gets the portal login. For most towns it's the Treasurer/Collector.</span></div>
            <div class="pt-grid-2">
              <label class="pt-field"><span class="pt-label">Full name *</span><input v-model.trim="form.contactName" class="pt-input" placeholder="Mary Walsh" /></label>
              <label class="pt-field"><span class="pt-label">Title</span>
                <select v-model="form.contactTitle" class="pt-select"><option v-for="t in titles" :key="t" :value="t">{{ t }}</option></select>
              </label>
              <label class="pt-field"><span class="pt-label">Email *</span><input v-model.trim="form.contactEmail" class="pt-input" type="email" placeholder="mwalsh@westbrook-ma.gov" /></label>
              <label class="pt-field"><span class="pt-label">Phone</span><input v-model.trim="form.phone" class="pt-input" type="tel" placeholder="(508) 555-0142" /></label>
            </div>
            <label class="pt-field">
              <span class="pt-label">Send invoices to <em>(if not the contact, e.g. accounts payable)</em></span>
              <input v-model.trim="form.billingEmail" class="pt-input" type="email" :placeholder="form.contactEmail || 'ap@westbrook-ma.gov'" />
            </label>
            <p v-if="form.contactEmail && !emailOk" class="pt-hint bad">That email doesn't look right.</p>
          </div>

          <!-- 3. Services -->
          <div v-if="step === 2" class="panel">
            <span class="pt-label">What will {{ form.cityName || "the city" }} collect or run through Municipal Collectionz?</span>
            <div class="chips">
              <button v-for="s in services" :key="s" type="button" class="chip" :class="{ on: form.services.includes(s) }" :aria-pressed="form.services.includes(s) ? 'true' : 'false'" @click="toggle(form.services, s)">
                <SiteIcon v-if="form.services.includes(s)" name="check" :size="14" /> {{ s }}
              </button>
            </div>
            <label class="pt-field">
              <span class="pt-label">Notes for the setup team <em>(optional)</em></span>
              <textarea v-model="form.notes" class="pt-textarea" rows="4" placeholder="Current billing software, when their bills go out, anything promised in the demo…"></textarea>
            </label>
          </div>

          <!-- 4. Contract -->
          <div v-if="step === 3" class="panel">
            <div class="plans">
              <button v-for="p in planTypes" :key="p.value" type="button" class="plan" :class="{ on: form.planType === p.value }" @click="form.planType = p.value">
                <b>{{ p.label }}</b><span>{{ p.hint }}</span>
              </button>
            </div>

            <div v-if="form.planType === 'subscription'" class="pt-field">
              <span class="pt-label">Size tier <em>{{ form.population ? `(suggested for ${Number(form.population).toLocaleString()} people)` : "" }}</em></span>
              <div class="tiers">
                <button v-for="(t, i) in tiers" :key="t.label" type="button" class="tier" :class="{ on: form.tier === i }" @click="pickTier(i)">
                  <b>{{ t.label }}</b>
                  <span>{{ t.custom ? "Custom pricing" : `${money(t.setupFee, { maximumFractionDigits: 0 })} setup · ${money(t.yearlyFee, { maximumFractionDigits: 0 })}/yr` }}</span>
                </button>
              </div>
            </div>

            <div class="pt-grid-2">
              <label class="pt-field"><span class="pt-label">Setup fee ($)</span><input v-model.number="form.setupFee" class="pt-input" type="number" min="0" step="100" /></label>
              <label v-if="form.planType === 'subscription'" class="pt-field"><span class="pt-label">Yearly fee ($)</span><input v-model.number="form.yearlyFee" class="pt-input" type="number" min="0" step="100" /></label>
              <label class="pt-field"><span class="pt-label">Contract length (years)</span><input v-model.number="form.contractYears" class="pt-input" type="number" min="1" max="10" /></label>
              <label class="pt-field"><span class="pt-label">Contract start</span><input v-model="form.startDate" class="pt-input" type="date" /></label>
            </div>

            <div class="pt-field">
              <span class="pt-label">How the city pays you</span>
              <div class="plans">
                <button v-for="b in billingMethods" :key="b.value" type="button" class="plan" :class="{ on: form.billingMethod === b.value, off: b.value === 'card' && !stripeKey }" :disabled="b.value === 'card' && !stripeKey" @click="form.billingMethod = b.value">
                  <b>{{ b.label }}</b><span>{{ b.value === "card" && !stripeKey ? "Add your Stripe publishable key to turn this on." : b.hint }}</span>
                </button>
              </div>
            </div>

            <!-- Officer phones & printers -->
            <div class="pt-field hw">
              <span class="pt-label">Officer phones &amp; printers <em>{{ hasParking ? "(for the parking handheld)" : "(only needed for parking)" }}</em></span>
              <div class="plans hw-plans">
                <button v-for="o in hardwareOptions" :key="o.value" type="button" class="plan" :class="{ on: form.hardware.option === o.value }" @click="form.hardware.option = o.value">
                  <b>{{ o.label }}</b><span>{{ o.hint }}</span>
                </button>
              </div>
              <div v-if="form.hardware.option !== 'none'" class="pt-grid-2 hw-counts">
                <label class="pt-field"><span class="pt-label">Printers</span><input v-model.number="form.hardware.printers" class="pt-input" type="number" min="0" max="50" /></label>
                <label class="pt-field"><span class="pt-label">Officer phones</span><input v-model.number="form.hardware.phones" class="pt-input" type="number" min="0" max="50" /></label>
                <label v-if="form.hardware.option === 'at_cost' && form.hardware.phones > 0" class="pt-field"><span class="pt-label">Cost per phone ($)</span><input v-model.number="form.hardware.phoneCost" class="pt-input" type="number" min="0" step="10" placeholder="What you'll pay" /></label>
              </div>
              <p v-if="form.hardware.option === 'at_cost'" class="pt-hint">
                {{ hw.printerModel }} (part {{ hw.printerPart }}) at {{ money(hw.printerCost) }} each.
                <b>Hardware total: {{ money(hardwareTotal) }}</b><template v-if="form.hardware.phones > 0 && !form.hardware.phoneCost"> plus phones (add the cost per phone)</template>.
              </p>
              <p v-else-if="form.hardware.option === 'city'" class="pt-hint">Send them the exact model: {{ hw.printerModel }}, part {{ hw.printerPart }}.</p>
              <p v-else-if="form.hardware.option === 'included'" class="pt-hint" :class="{ bad: form.hardware.printers > hw.includedCap }">
                <template v-if="form.hardware.printers > hw.includedCap">Only {{ hw.includedCap }} printers are included. Lower the count, or bill the extra {{ form.hardware.printers - hw.includedCap }} at cost from the city page.</template>
                <template v-else>About {{ money(form.hardware.printers * hw.printerCost) }} of printers comes out of the {{ money(form.setupFee || 0) }} setup fee. Phones are still the city's.</template>
              </p>
              <div v-if="suggestIncluded" class="pt-notice info hw-tip">
                <SiteIcon name="trophy" :size="17" />
                <span>Closing a bigger town? "Printers included" is yours to offer on this tier.</span>
              </div>
              <p class="pt-hint agreement">Agreement wording: “{{ hw.agreementLine }}”</p>
            </div>

            <!-- Card (only for card billing) -->
            <div v-if="form.billingMethod === 'card'" class="card-box">
              <div v-if="card.saved" class="saved">
                <SiteIcon name="check" :size="17" /> <span>{{ card.brand }} •••• {{ card.last4 }} saved</span>
                <button type="button" class="pt-btn pt-btn-ghost pt-btn-sm" @click="changeCard">Change</button>
              </div>
              <template v-else>
                <span class="pt-label">Card details</span>
                <div v-if="card.initializing" class="card-loading">Loading the secure card form…</div>
                <div id="stripe-card-element" class="card-el" :class="{ hidden: card.initializing }"></div>
                <p v-if="card.error" class="pt-hint bad">{{ card.error }}</p>
                <button type="button" class="pt-btn pt-btn-ink pt-btn-sm" :disabled="!card.complete || card.saving || card.initializing" @click="saveCard">{{ card.saving ? "Saving…" : "Save card" }}</button>
                <p class="pt-hint">Card details go straight to Stripe. They never touch your database.</p>
              </template>
            </div>

            <label v-if="form.setupFee > 0" class="pt-switch invoice-now">
              <input v-model="form.sendSetupInvoice" type="checkbox" /><span class="pt-switch-track"></span>
              Email the {{ money(form.setupFee) }} setup fee invoice when I finish<template v-if="hardwareOnInvoice">, with {{ money(hardwareTotal) }} of hardware at cost on it</template>
            </label>
          </div>

          <!-- 5. Review -->
          <div v-if="step === 4" class="panel">
            <div class="review">
              <div class="rv"><div class="pt-label">City</div><p><b>{{ form.cityName }}</b>, {{ form.state }}<br />{{ form.population ? `${Number(form.population).toLocaleString()} people` : "Population not given" }}<br /><span class="pt-muted">{{ addressLine || "No address" }}</span></p></div>
              <div class="rv"><div class="pt-label">Login for</div><p><b>{{ form.contactName }}</b>, {{ form.contactTitle }}<br />{{ form.contactEmail }}<br /><span class="pt-muted">{{ form.phone || "No phone" }}</span></p></div>
              <div class="rv"><div class="pt-label">Contract</div><p>
                <b>{{ form.planType === "fee_only" ? "Fee-only plan" : "Yearly subscription" }}</b><br />
                Setup {{ money(form.setupFee || 0) }}<template v-if="form.planType === 'subscription'"> · {{ money(form.yearlyFee || 0) }}/yr</template><br />
                {{ form.contractYears }} years<template v-if="form.startDate"> from {{ formatDate(form.startDate) }}</template><br />
                <span class="pt-muted">{{ form.billingMethod === "card" ? `Card ${card.saved ? `•••• ${card.last4}` : "(not saved yet)"}` : `Invoices to ${form.billingEmail || form.contactEmail}` }}</span>
              </p></div>
              <div class="rv"><div class="pt-label">Services</div><p>{{ form.services.join(", ") || "None picked" }}</p></div>
              <div class="rv"><div class="pt-label">Phones &amp; printers</div><p><b>{{ hardwareOptionLabel }}</b><template v-if="form.hardware.option !== 'none' && hardwareSummary"><br />{{ hardwareSummary }}</template><template v-if="form.hardware.option === 'at_cost'"><br /><span class="pt-muted">{{ money(hardwareTotal) }} at cost{{ form.sendSetupInvoice && form.setupFee > 0 ? ", on the setup invoice" : ", bill it from the city page" }}</span></template></p></div>
            </div>
            <div v-if="problems.length" class="pt-notice warn"><SiteIcon name="alert" :size="17" /><span>Before you create it: {{ problems.join(" · ") }}</span></div>
            <div v-if="error" class="pt-notice error"><SiteIcon name="alert" :size="17" /><span>{{ error }}</span></div>
          </div>

          <div class="nav">
            <button v-if="step > 0" type="button" class="pt-btn pt-btn-ghost" @click="step--"><SiteIcon name="arrowLeft" :size="16" /> Back</button>
            <span class="grow"></span>
            <button v-if="step < steps.length - 1" type="button" class="pt-btn pt-btn-primary" :disabled="!stepOk(step)" @click="next">
              Continue <SiteIcon name="arrowRight" :size="16" />
            </button>
            <button v-else type="button" class="pt-btn pt-btn-primary" :disabled="submitting || problems.length > 0" @click="submit">
              <SiteIcon name="check" :size="16" /> {{ submitting ? "Creating the city…" : "Create city & send welcome email" }}
            </button>
          </div>
        </section>

        <!-- Checklist -->
        <aside class="pt-card side">
          <div class="side-head">Checklist</div>
          <ul class="checks">
            <li :class="{ ok: stepOk(0) }"><SiteIcon :name="stepOk(0) ? 'check' : 'dot'" :size="16" /> City name and state</li>
            <li :class="{ ok: stepOk(1) }"><SiteIcon :name="stepOk(1) ? 'check' : 'dot'" :size="16" /> Contact name and email</li>
            <li :class="{ ok: form.services.length }"><SiteIcon :name="form.services.length ? 'check' : 'dot'" :size="16" /> What they'll collect</li>
            <li :class="{ ok: contractOk }"><SiteIcon :name="contractOk ? 'check' : 'dot'" :size="16" /> Contract amounts</li>
          </ul>
          <div class="side-note">
            <b>What happens when you finish</b>
            <ol>
              <li>The city's portal and login are created by the <code>createCustomerAccount</code> function.</li>
              <li>{{ form.contactName || "The contact" }} gets an email to set a password.</li>
              <li>The rollout starts at <b>Not Started</b>. Move it to Kickoff from Rollouts.</li>
            </ol>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import "@/styles/portal.css";
import { auth, db, functions } from "@/firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { loadStripe } from "@stripe/stripe-js";
import PortalTopBar from "@/components/PortalTopBar.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import { STRIPE_PUBLISHABLE_KEY } from "@/config/site";
import { CITY_SERVICES, CONTACT_TITLES, PRICE_TIERS, PLAN_TYPES, BILLING_METHODS, HARDWARE, HARDWARE_OPTIONS, hardwareLabel, hardwareCounts, suggestTier } from "@/config/cityPortal";
import { money, formatDate } from "@/config/format";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LEAD_LABELS = { new: "New", contacted: "Contacted", qualified: "Demo booked", proposal: "Proposal sent", won: "Won", lost: "Lost" };

function emptyForm() {
  const t = PRICE_TIERS[1];
  return {
    leadId: "",
    cityName: "",
    state: "",
    population: null,
    website: "",
    street: "",
    addrCity: "",
    zip: "",
    contactName: "",
    contactTitle: "Treasurer/Collector",
    contactEmail: "",
    phone: "",
    billingEmail: "",
    services: ["Real estate tax", "Motor vehicle excise", "Water & sewer"],
    notes: "",
    planType: "subscription",
    tier: 1,
    setupFee: t.setupFee,
    yearlyFee: t.yearlyFee,
    contractYears: 3,
    startDate: new Date().toISOString().slice(0, 10),
    billingMethod: "invoice",
    sendSetupInvoice: true,
    hardware: { option: "none", printers: 2, phones: 0, phoneCost: null },
  };
}

export default {
  name: "StaffOnboardCustomer",
  components: { PortalTopBar, SiteIcon },
  data() {
    return {
      steps: ["City", "Contact", "Services", "Contract", "Review"],
      step: 0,
      maxReached: 0,
      form: emptyForm(),
      tierTouched: false,
      services: CITY_SERVICES,
      titles: CONTACT_TITLES,
      tiers: PRICE_TIERS,
      planTypes: PLAN_TYPES,
      billingMethods: BILLING_METHODS,
      hardwareOptions: HARDWARE_OPTIONS,
      hw: HARDWARE,
      stripeKey: STRIPE_PUBLISHABLE_KEY,
      card: { initializing: false, saving: false, complete: false, saved: false, error: "", brand: "", last4: "", customerId: "", clientSecret: "", paymentMethodId: "" },
      leads: [],
      barRole: "staff",
      submitting: false,
      error: "",
      done: null,
    };
  },
  computed: {
    hasParking() {
      return this.form.services.includes("Parking tickets");
    },
    hardwareTotal() {
      const h = this.form.hardware;
      if (h.option !== "at_cost") return 0;
      return (Number(h.printers) || 0) * HARDWARE.printerCost + (Number(h.phones) || 0) * (Number(h.phoneCost) || 0);
    },
    hardwareOnInvoice() {
      return this.form.hardware.option === "at_cost" && this.hardwareTotal > 0 && this.form.sendSetupInvoice && this.form.setupFee > 0;
    },
    hardwareOptionLabel() {
      return hardwareLabel(this.form.hardware);
    },
    hardwareSummary() {
      return hardwareCounts(this.form.hardware);
    },
    suggestIncluded() {
      return this.hasParking && this.form.planType === "subscription" && this.form.tier >= HARDWARE.includedFromTier && this.form.hardware.option !== "included";
    },
    emailOk() {
      return EMAIL.test(this.form.contactEmail);
    },
    addressLine() {
      const f = this.form;
      return [f.street, f.addrCity || f.cityName.replace(/^(Town|City) of /i, ""), [f.state, f.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    },
    contractOk() {
      const f = this.form;
      return f.contractYears > 0 && (f.planType === "fee_only" || f.yearlyFee > 0);
    },
    problems() {
      const p = [];
      if (!this.stepOk(0)) p.push("add the city name and state");
      if (!this.stepOk(1)) p.push("add the contact's name and a valid email");
      if (this.form.billingEmail && !EMAIL.test(this.form.billingEmail)) p.push("fix the invoice email");
      if (!this.contractOk) p.push("set the yearly fee");
      if (this.form.billingMethod === "card" && !this.card.saved) p.push("save the card, or switch to emailed invoices");
      return p;
    },
  },
  watch: {
    // Parking means the handheld, so default to ordering printers at cost.
    hasParking(on) {
      if (on && this.form.hardware.option === "none") this.form.hardware.option = "at_cost";
      if (!on && this.form.hardware.option === "at_cost") this.form.hardware.option = "none";
    },
    "form.billingMethod"(v) {
      if (v === "card" && !this.card.saved && !this._stripe) this.$nextTick(this.initStripe);
    },
    step(v) {
      if (v > this.maxReached) this.maxReached = v;
      if (v === 3 && this.form.billingMethod === "card" && !this.card.saved) this.$nextTick(this.initStripe);
    },
  },
  mounted() {
    this._unsubAuth = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        this.$router.push("/login");
        return;
      }
      try {
        const me = await getDoc(doc(db, "users", u.uid));
        this.barRole = me.exists() && me.data().role === "Admin" ? "admin" : "staff";
        const snap = await getDocs(query(collection(db, "leads"), where("ownerUid", "==", u.uid)));
        this.leads = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((l) => !["won", "lost"].includes(l.status))
          .sort((a, b) => (a.companyName || "").localeCompare(b.companyName || ""));
        const pre = this.$route.query.lead;
        if (pre && this.leads.some((l) => l.id === pre)) {
          this.form.leadId = pre;
          this.applyLead();
        }
      } catch (e) {
        console.error("Couldn't load leads:", e);
      }
    });
  },
  beforeDestroy() {
    if (this._unsubAuth) this._unsubAuth();
    this.destroyCard();
  },
  methods: {
    // The line that appears on the invoice, e.g. "Officer hardware at cost: 3 × Zebra ZQ320 Plus printer, 2 phones".
    hardwareDescription() {
      const h = this.form.hardware;
      const parts = [];
      if (h.printers) parts.push(`${h.printers} × ${HARDWARE.printerModel.replace(/ \(.*\)$/, "")} printer`);
      if (h.phones && h.phoneCost) parts.push(`${h.phones} × officer phone`);
      return parts.length ? `Officer hardware at cost: ${parts.join(", ")}` : "";
    },
    money,
    formatDate,
    stageName(s) {
      return LEAD_LABELS[s] || s;
    },
    toggle(list, v) {
      const i = list.indexOf(v);
      if (i === -1) list.push(v);
      else list.splice(i, 1);
    },
    stepOk(i) {
      const f = this.form;
      if (i === 0) return !!(f.cityName && f.state);
      if (i === 1) return !!(f.contactName && this.emailOk) && (!f.billingEmail || EMAIL.test(f.billingEmail));
      if (i === 3) return this.contractOk;
      return true;
    },
    next() {
      if (this.stepOk(this.step)) this.step++;
    },
    applyLead() {
      const l = this.leads.find((x) => x.id === this.form.leadId);
      if (!l) return;
      const f = this.form;
      f.cityName = l.companyName || f.cityName;
      f.state = l.state || f.state;
      f.contactName = l.contactName || f.contactName;
      if (CONTACT_TITLES.includes(l.contactTitle)) f.contactTitle = l.contactTitle;
      f.contactEmail = l.email || f.contactEmail;
      f.phone = l.phone || f.phone;
      // Leads store population as a range ("10,000 - 25,000"): use the top of it.
      const nums = String(l.population || "").match(/[\d,]+/g);
      if (nums && !f.population) {
        f.population = Number(nums[nums.length - 1].replace(/,/g, "")) || null;
        if (!this.tierTouched) this.applyTier();
      }
      if (l.notes && !f.notes) f.notes = l.notes;
    },
    applyTier() {
      this.setTier(suggestTier(this.form.population));
    },
    pickTier(i) {
      this.tierTouched = true;
      this.setTier(i);
    },
    setTier(i) {
      const t = PRICE_TIERS[i];
      this.form.tier = i;
      if (!t.custom) {
        this.form.setupFee = t.setupFee;
        this.form.yearlyFee = t.yearlyFee;
      }
    },

    // ---------- Stripe card (only for "card on file" billing) ----------
    async initStripe() {
      if (!this.stripeKey || this._stripe || this.card.initializing) return;
      if (!this.emailOk) {
        this.card.error = "Add the contact's email first (step 2).";
        return;
      }
      this.card.initializing = true;
      this.card.error = "";
      try {
        const f = this.form;
        const [stripe, setup] = await Promise.all([
          loadStripe(this.stripeKey),
          httpsCallable(functions, "createStripeSetupIntent")({
            email: f.billingEmail || f.contactEmail,
            name: f.cityName,
            cityName: f.cityName,
            address: { street: f.street, city: f.addrCity || f.cityName, state: f.state, zip: f.zip, country: "US" },
          }),
        ]);
        if (!stripe) throw new Error("Stripe failed to load");
        this._stripe = stripe;
        this.card.customerId = setup.data.customerId;
        this.card.clientSecret = setup.data.clientSecret;
        this._elements = stripe.elements();
        this._cardEl = this._elements.create("card", {
          style: { base: { color: "#0e2236", fontFamily: "Public Sans, -apple-system, sans-serif", fontSize: "15px", "::placeholder": { color: "#9aa3ad" } }, invalid: { color: "#b4452f" } },
        });
        this._cardEl.on("change", (e) => {
          this.card.complete = e.complete;
          this.card.error = e.error ? e.error.message : "";
        });
        this.card.initializing = false;
        await this.$nextTick();
        this._cardEl.mount("#stripe-card-element");
      } catch (e) {
        console.error("Stripe init failed:", e);
        this.card.error = "Couldn't load the secure card form. You can use emailed invoices instead.";
        this.card.initializing = false;
      }
    },
    async saveCard() {
      if (!this._stripe || !this._cardEl) return;
      this.card.saving = true;
      this.card.error = "";
      try {
        const { setupIntent, error } = await this._stripe.confirmCardSetup(this.card.clientSecret, {
          payment_method: { card: this._cardEl, billing_details: { name: this.form.contactName || this.form.cityName, email: this.form.billingEmail || this.form.contactEmail } },
        });
        if (error) {
          this.card.error = error.message || "The card was declined. Check the details and try again.";
          return;
        }
        this.card.paymentMethodId = setupIntent.payment_method;
        const res = await httpsCallable(functions, "confirmStripePaymentMethod")({ customerId: this.card.customerId, paymentMethodId: this.card.paymentMethodId });
        const names = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover" };
        this.card.brand = names[res.data.brand] || res.data.brand;
        this.card.last4 = res.data.last4;
        this.card.saved = true;
        this._cardEl.unmount();
      } catch (e) {
        console.error("Save card failed:", e);
        this.card.error = "Something went wrong saving the card. Please try again.";
      } finally {
        this.card.saving = false;
      }
    },
    changeCard() {
      this.destroyCard();
      this.card = { ...this.card, saved: false, complete: false, error: "", brand: "", last4: "", paymentMethodId: "" };
      this.$nextTick(this.initStripe);
    },
    destroyCard() {
      if (this._cardEl) {
        try {
          this._cardEl.destroy();
        } catch (e) {
          /* already gone */
        }
      }
      this._cardEl = null;
      this._elements = null;
      this._stripe = null;
    },

    async submit() {
      if (this.problems.length) return;
      const f = this.form;
      this.submitting = true;
      this.error = "";
      try {
        const res = await httpsCallable(functions, "createCustomerAccount")({
          contactEmail: f.contactEmail,
          contactName: f.contactName,
          contactTitle: f.contactTitle,
          phone: f.phone,
          billingEmail: f.billingEmail,
          cityName: f.cityName,
          state: f.state,
          population: f.population || null,
          website: f.website,
          address: { street: f.street, city: f.addrCity || f.cityName.replace(/^(Town|City) of /i, ""), state: f.state, zip: f.zip, country: "US" },
          services: f.services,
          notes: f.notes,
          leadId: f.leadId || null,
          plan: {
            type: f.planType,
            tierLabel: f.planType === "subscription" ? PRICE_TIERS[f.tier].label : "",
            setupFee: Number(f.setupFee) || 0,
            yearlyFee: f.planType === "subscription" ? Number(f.yearlyFee) || 0 : 0,
            contractYears: Number(f.contractYears) || 3,
            startDate: f.startDate,
            billingMethod: f.billingMethod,
            hardware: {
              option: f.hardware.option,
              printers: f.hardware.option === "none" ? 0 : Number(f.hardware.printers) || 0,
              phones: f.hardware.option === "none" ? 0 : Number(f.hardware.phones) || 0,
              totalAtCost: this.hardwareTotal,
              description: this.hardwareDescription(),
            },
          },
          stripeCustomerId: this.card.customerId || null,
          stripePaymentMethodId: this.card.saved ? this.card.paymentMethodId : null,
          cardBrand: this.card.saved ? this.card.brand : null,
          cardLast4: this.card.saved ? this.card.last4 : null,
          sendSetupInvoice: !!(f.sendSetupInvoice && f.setupFee > 0),
        });
        this.done = res.data;
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        console.error("Onboarding failed:", e);
        this.error = e.message || "Couldn't create the city. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
    startOver() {
      const usedLead = this.form.leadId;
      this.destroyCard();
      this.form = emptyForm();
      this.card = { initializing: false, saving: false, complete: false, saved: false, error: "", brand: "", last4: "", customerId: "", clientSecret: "", paymentMethodId: "" };
      this.tierTouched = false;
      this.done = null;
      this.step = 0;
      this.maxReached = 0;
      this.leads = this.leads.filter((l) => l.id !== usedLead);
    },
  },
};
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 22px;
  align-items: start;
}

.grid .pt-card + .pt-card {
  margin-top: 0;
}

.wizard {
  padding: 0;
  overflow: hidden;
}

.steps {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
  border-bottom: 1px solid var(--rule);
  background: var(--paper-2);
}

.mc-portal .steps button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: var(--muted);
  font-family: var(--sans);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.mc-portal .steps button:disabled {
  cursor: not-allowed;
}

.steps .n {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border: 1.5px solid var(--rule-strong);
  border-radius: 50%;
  font-size: 12px;
}

.mc-portal .steps li.on button {
  border-bottom-color: var(--green);
  background: var(--card);
  color: var(--ink);
}

.steps li.on .n {
  border-color: var(--green);
  background: var(--green);
  color: #fff;
}

.steps li.done .n {
  border-color: var(--green);
  color: var(--green);
}

.mc-portal .steps li.done button {
  color: var(--ink-2);
}

.l {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel {
  display: grid;
  gap: 16px;
  padding: 24px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mc-portal .chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border: 1.5px solid var(--rule-strong);
  border-radius: 999px;
  background: #fff;
  color: var(--ink-2);
  font-family: var(--sans);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.mc-portal .chip.on {
  border-color: var(--green);
  background: var(--green-tint);
  color: var(--green-2);
}

.plans,
.tiers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.tiers {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.mc-portal .plan,
.mc-portal .tier {
  display: grid;
  gap: 4px;
  padding: 14px;
  border: 1.5px solid var(--rule-strong);
  border-radius: 8px;
  background: #fff;
  color: var(--ink);
  font-family: var(--sans);
  text-align: left;
  cursor: pointer;
  min-width: 0;
}

.plan span,
.tier span {
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--muted);
}

.mc-portal .plan.on,
.mc-portal .tier.on {
  border-color: var(--green);
  background: var(--green-tint);
  box-shadow: inset 0 0 0 1px var(--green);
}

.mc-portal .plan.off {
  opacity: 0.6;
  cursor: not-allowed;
}

.card-box {
  display: grid;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--rule);
  border-left: 3px solid var(--ink);
  border-radius: 6px;
  background: var(--paper);
}

.card-el {
  padding: 12px;
  border: 1.5px solid var(--rule-strong);
  border-radius: 6px;
  background: #fff;
}

.card-el.hidden {
  display: none;
}

.card-loading {
  font-size: 13.5px;
  color: var(--muted);
}

.card-box .pt-btn {
  justify-self: start;
}

.saved {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--green-2);
  font-weight: 700;
}

.saved span {
  flex: 1;
}

.invoice-now {
  align-items: flex-start;
}

.review {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.rv {
  padding: 14px 16px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: var(--paper);
  min-width: 0;
}

.rv p {
  margin-top: 6px;
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.nav {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--rule);
  background: var(--paper);
}

.grow {
  flex: 1;
}

.bad {
  color: var(--red-ink);
}

.side {
  position: sticky;
  top: 86px;
}

.side-head {
  font-family: var(--serif);
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.checks {
  display: grid;
  gap: 10px;
  margin: 0 0 18px;
  padding: 0;
  list-style: none;
  font-size: 14px;
  color: var(--muted);
}

.checks li {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checks li.ok {
  color: var(--green-2);
  font-weight: 600;
}

.side-note {
  padding: 14px;
  border-radius: 8px;
  background: var(--ink);
  color: var(--on-navy-2);
  font-size: 13.5px;
  line-height: 1.6;
}

.side-note b {
  color: var(--on-navy);
}

.side-note ol {
  margin: 8px 0 0;
  padding-left: 18px;
}

.side-note code {
  color: #7cc4a5;
  font-size: 12.5px;
}

.done {
  position: relative;
  max-width: 760px;
  padding: 32px;
  border-top: 4px solid var(--green);
}

.mc-portal .done h2 {
  font-size: 1.8rem;
}

.done > p {
  margin-top: 6px;
  color: var(--ink-2);
}

.stamp {
  position: absolute;
  top: 22px;
  right: 24px;
  padding: 6px 12px;
  border: 3px solid var(--green);
  border-radius: 6px;
  color: var(--green);
  font-family: var(--serif);
  font-weight: 700;
  letter-spacing: 0.12em;
  transform: rotate(-6deg);
  opacity: 0.85;
}

.done-list {
  display: grid;
  gap: 8px;
  margin: 20px 0;
}

.done-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 1000px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .side {
    position: static;
  }
  .tiers {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .mc-portal .steps button {
    flex-direction: column;
    gap: 4px;
    padding: 10px 4px;
    font-size: 11.5px;
  }
  .plans,
  .tiers,
  .review {
    grid-template-columns: minmax(0, 1fr);
  }
  .panel {
    padding: 18px;
  }
  .nav {
    padding: 14px 18px;
  }
  .stamp {
    position: static;
    display: inline-block;
    margin-bottom: 12px;
  }
  .done {
    padding: 22px;
  }
}

/* ---------- officer hardware ---------- */
.hw {
  padding-top: 14px;
  border-top: 1px dashed var(--rule-strong);
}
.hw-plans {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hw-counts {
  margin-top: 4px;
}
.hw-tip {
  margin-top: 4px;
}
.agreement {
  font-style: italic;
}
@media (max-width: 640px) {
  .hw-plans {
    grid-template-columns: 1fr;
  }
}
</style>